import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Typography, message, Card, Input, Select, Button, Space } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import EditableDataTable from '../components/EditableDataTable';
import AxiosOICStat from '../AxiosOICStat';
import { getIndicatorName } from '../utils/indicatorName';

const { Title } = Typography;
const { Option } = Select;

export const DataManagement = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [filters, setFilters] = useState({
        c_codes: [],
        ind_codes: [],
        years: [],
    });

    const indicatorsState = useSelector(state => Object.values(state.indicators.value));
    const countriesState = useSelector(state => Object.values(state.countries.value));

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 1970; year <= currentYear; year++) {
            years.push(year);
        }
        return years.reverse();
    }, []);

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize, filters);
    }, []);

    const fetchData = async (page, perPage, currentFilters = filters) => {
        setLoading(true);
        try {
            const requestBody = {
                page: page,
                per_page: perPage,
            };
            
            if (currentFilters.c_codes && currentFilters.c_codes.length > 0) {
                requestBody.c_codes = currentFilters.c_codes;
            }
            if (currentFilters.ind_codes && currentFilters.ind_codes.length > 0) {
                requestBody.ind_codes = currentFilters.ind_codes;
            }
            if (currentFilters.years && currentFilters.years.length > 0) {
                requestBody.years = currentFilters.years;
            }

            const response = await AxiosOICStat.post('/data/paginated', requestBody);
            setData(response.data.items);
            setPagination({
                current: response.data.page,
                pageSize: response.data.per_page,
                total: response.data.total,
            });
        } catch (error) {
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        fetchData(newPagination.current, newPagination.pageSize);
    };

    const handleSave = async (row) => {
        try {
            await AxiosOICStat.put('/data/update-single-data-point/', row);
            message.success('Data updated successfully');
            fetchData(pagination.current, pagination.pageSize, filters);
        } catch (error) {
            message.error('Failed to update data');
            throw error;
        }
    };

    const handleDelete = async (row) => {
        try {
            await AxiosOICStat.delete('/data/delete-single-data-point/', {
                data: {
                    c_code: row.c_code,
                    ind_code: row.ind_code,
                    d_year: row.d_year,
                },
            });
            message.success('Data deleted successfully');
            fetchData(pagination.current, pagination.pageSize, filters);
        } catch (error) {
            message.error('Failed to delete data');
        }
    };

    const handleAdd = async (row) => {
        try {
            await AxiosOICStat.post('/data/create-single-data-point/', row);
            message.success('Data added successfully');
            fetchData(pagination.current, pagination.pageSize, filters);
        } catch (error) {
            message.error('Failed to add data');
            throw error;
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(1, pagination.pageSize, filters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            c_codes: [],
            ind_codes: [],
            years: [],
        };
        setFilters(clearedFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(1, pagination.pageSize, clearedFilters);
    };

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Title level={2}>Data Management</Title>
                </Col>
                <Col span={24}>
                    <Card title="Filters" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Countries"
                                    value={filters.c_codes}
                                    onChange={(value) => handleFilterChange('c_codes', value)}
                                    style={{ width: '100%' }}
                                    showSearch
                                    autoClearSearchValue={false}
                                    filterOption={(input, option) => {
                                        const country = countriesState.find(c => c.c_code === option.value);
                                        if (!country) return false;
                                        const searchTerm = input.toLowerCase();
                                        return (
                                            country.c_short_name_eng?.toLowerCase().includes(searchTerm) ||
                                            country.c_code?.toString().includes(searchTerm)
                                        );
                                    }}
                                    maxTagCount="responsive"
                                >
                                    {countriesState.map(country => (
                                        <Option key={country.c_code} value={country.c_code}>
                                            {country.c_short_name_eng} ({country.c_code})
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Indicators"
                                    value={filters.ind_codes}
                                    onChange={(value) => handleFilterChange('ind_codes', value)}
                                    style={{ width: '100%' }}
                                    showSearch
                                    autoClearSearchValue={false}
                                    filterOption={(input, option) => {
                                        const indicator = indicatorsState.find(ind => ind.ind_code === option.value);
                                        if (!indicator) return false;
                                        const searchTerm = input.toLowerCase();
                                        return (
                                            getIndicatorName(indicator)?.toLowerCase().includes(searchTerm) ||
                                            indicator.ind_code?.toString().includes(searchTerm)
                                        );
                                    }}
                                    maxTagCount="responsive"
                                >
                                    {indicatorsState.map(indicator => (
                                        <Option key={indicator.ind_code} value={indicator.ind_code}>
                                            {getIndicatorName(indicator)} ({indicator.ind_code})
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Years"
                                    value={filters.years}
                                    onChange={(value) => handleFilterChange('years', value)}
                                    style={{ width: '100%' }}
                                    showSearch
                                    maxTagCount="responsive"
                                >
                                    {yearOptions.map(year => (
                                        <Option key={year} value={year}>
                                            {year}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        onClick={handleApplyFilters}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col span={24}>
                    <EditableDataTable
                        data={data}
                        loading={loading}
                        pagination={pagination}
                        onTableChange={handleTableChange}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onAdd={handleAdd}
                    />
                </Col>
            </Row>
        </div>
    );
};
